import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFig } from '../../session/FigProvider.js';
import { NoRecordingError } from '../../core/api/errors.js';
import type { ConversationDetail as Detail, Transcript } from '../../core/api/types.js';
import { ErrorState, RowSkeleton } from '../../components/States.js';
import { Player, clock, spoken } from '../../components/Player.js';

/**
 * One conversation: summary, player, transcript.
 *
 * Three findings from the premium derivation are settled here rather than left to a later pass:
 *   S4 — detail and transcript are fetched TOGETHER, not one after the other, so the reader
 *        waits once instead of twice
 *   S5 — the recording starts downloading the moment the page opens, while they read the
 *        summary, because the next thing anyone does here is press play
 *   S1/S2 — the bytes are cached by conversation id, so a replay is instant and a long call
 *        cannot die when its 15-minute link expires
 */
export function ConversationDetail() {
  const { id = '' } = useParams();
  const { client } = useFig();
  const [data, setData] = useState<{ conversation: Detail; transcript: Transcript } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [noRecording, setNoRecording] = useState(false);
  const [audioError, setAudioError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setError(null); setData(null); setAudioUrl(null); setNoRecording(false); setAudioError(null);
    try {
      const result = await client.getConversationWithTranscript(id);
      setData(result);
      if (!result.conversation.hasAudio) { setNoRecording(true); return; }
      // S5: begin fetching the recording now, without blocking the page on it.
      //
      // The whole conversation goes in, not the id. The cache key is derived from what the
      // RECORDING is (duration + waveform peaks), because a conversation id is stable while its
      // audio is not: a reseed, a re-sync or a provider re-processing the call all produce new
      // bytes under the same id. Keying by id served the old audio forever, which is how a voice
      // that had been removed kept talking under a corrected transcript (2026-09-16).
      client.getAudioUrl(result.conversation)
        .then((a) => setAudioUrl(a.url))
        .catch((e) => {
          if (e instanceof NoRecordingError) setNoRecording(true);
          else setAudioError(e);
        });
    } catch (e) {
      setError(e);
    }
  }, [client, id]);

  useEffect(() => { void load(); }, [load]);

  if (error !== null) {
    return <main className="page"><ErrorState error={error} onRetry={() => void load()} /></main>;
  }
  if (data === null) {
    return <main className="page"><RowSkeleton count={5} /></main>;
  }

  const { conversation: c, transcript } = data;

  return (
    <main className="page">
      <p><Link className="linkbtn" to="/calls">Back to your calls</Link></p>
      <h1 className="page-title">{c.summary ?? 'Call'}</h1>
      <p className="row-meta">
        <span>{new Date(c.startedAt).toLocaleString()}</span>
        <span aria-hidden="true">·</span>
        <span aria-label={spoken(c.durationSeconds)}>{clock(c.durationSeconds)}</span>
        {c.outcome && <><span aria-hidden="true">·</span><span>{c.outcome}</span></>}
      </p>

      {/* A conversation with no recording is a NORMAL state for a text channel, never an error. */}
      {noRecording && (
        <p className="banner" role="status">
          Transcript only — this conversation came in by {c.channel ?? 'message'}, so there’s
          nothing to listen to.
        </p>
      )}
      {audioError !== null && <ErrorState error={audioError} onRetry={() => void load()} />}
      {!noRecording && audioError === null && (
        audioUrl
          ? <Player
              src={audioUrl} durationSeconds={c.durationSeconds} title={c.summary ?? 'this call'} peaks={c.waveformPeaks}
              // Every playback is logged, replays from the cache included (2026-10-20). Never
              // allowed to interrupt listening: a failed log is not the listener's problem.
              onPlaybackStart={() => { void client.recordPlayed(c.id).catch(() => {}); }}
              // And how much was heard (2026-10-21), so "listened to" means more than "pressed play".
              onListened={(s) => client.recordListened(c.id, s)}
            />
          : <p className="hint" role="status">Getting the recording ready…</p>
      )}

      <h2 className="state-title">Transcript</h2>
      {transcript.turns.length === 0 ? (
        <p className="state-body">No transcript was captured for this call.</p>
      ) : (
        <ol className="turns">
          {transcript.turns.map((t) => (
            <li key={t.id} className={`turn turn-${t.role.toLowerCase()}`}>
              <span className="turn-who">{t.role === 'Agent' ? 'Assistant' : 'Caller'}</span>
              <p className="turn-text">{t.text}</p>
            </li>
          ))}
        </ol>
      )}
    </main>
  );
}
